import { useState, useEffect, useCallback, useRef } from "react";
import type {
  CriteriaFilter,
  DateRangeFilter,
  ScoreFilter,
  TagFilter,
  TraceComment,
  TraceItem,
} from "../types";
import { listTraces } from "../api";
import { getImpersonationDomain } from "../../impersonation/storage";
import { readRequestCache, writeRequestCache } from "../utils/requestCache";

const PAGE_SIZE = 20;

/** The structured filter slice the Traces list is fetched under. Mirrors the
 * Insights filter surface so a chart drill-down lands on the same slice. */
export interface TracesFilterInput {
  filters: TagFilter[];
  criteriaFilter?: CriteriaFilter;
  dateRangeFilter?: DateRangeFilter;
  scoreFilter?: ScoreFilter;
}

/** Translate the filter slice into `listTraces` query params (tags use the
 * `key:value1,value2` wire format shared with /insights/aggregate). */
function toListParams(input: TracesFilterInput) {
  return {
    start_date: input.dateRangeFilter?.start_date,
    end_date: input.dateRangeFilter?.end_date,
    min_score: input.scoreFilter?.min_score,
    max_score: input.scoreFilter?.max_score,
    criteria: input.criteriaFilter?.values?.length
      ? input.criteriaFilter.values
      : undefined,
    tag: input.filters.length
      ? input.filters.map((f) => `${f.key}:${f.values.join(",")}`)
      : undefined,
  };
}

/** Page-1 slice cached for instant restore when toggling back to Traces. */
interface CachedTraces {
  traces: TraceItem[];
  nextPageToken: string | null;
}

/**
 * Cursor-paginated Traces list under the full structured filter set. The filter
 * slice is owned by the caller (useTracesFilters); this hook refetches page 1
 * whenever it changes and exposes next/prev pagination over the current slice.
 *
 * Page 1 is cached (stale-while-revalidate) keyed by the filter slice + scope,
 * so toggling Insights <-> Traces restores the last view instantly and
 * revalidates in the background instead of flashing a skeleton (#1402 follow-up).
 */
export function useTraces(input: TracesFilterInput) {
  // Params + content key, built before state so we can seed page 1 from cache.
  const listParams = toListParams(input);
  const listParamsRef = useRef(listParams);
  listParamsRef.current = listParams;
  // Stable content key: refetch only when the filter *values* change, not when
  // the caller passes a fresh object identity each render.
  const filterKey = JSON.stringify(listParams);
  // Cache key scoped by impersonation domain so scopes never share rows.
  const cacheKey = `traces:${getImpersonationDomain() || "self"}:${filterKey}`;
  const cacheKeyRef = useRef(cacheKey);
  cacheKeyRef.current = cacheKey;

  const [traces, setTraces] = useState<TraceItem[]>(
    () => readRequestCache<CachedTraces>(cacheKey)?.traces ?? [],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextPageToken, setNextPageToken] = useState<string | null>(
    () => readRequestCache<CachedTraces>(cacheKey)?.nextPageToken ?? null,
  );
  // Stack of tokens for pages we've visited — empty means we're on page 1.
  const [pageTokenStack, setPageTokenStack] = useState<string[]>([]);

  const hasPrev = pageTokenStack.length > 0;
  const hasNext = nextPageToken !== null;
  const currentPage = pageTokenStack.length + 1;

  // Monotonic request id so only the latest fetch applies its result — the
  // richer filter surface makes rapid changes (hence overlapping requests that
  // could resolve out of order) more reachable; a superseded response is dropped.
  const requestSeqRef = useRef(0);

  const fetchPage = useCallback(
    async (
      pageToken: string | undefined,
      stack: string[],
      background = false,
    ) => {
      const seq = ++requestSeqRef.current;
      // A background revalidate keeps the cached rows on screen — no spinner.
      if (!background) setLoading(true);
      setError(null);
      try {
        const result = await listTraces({
          page_token: pageToken,
          page_size: PAGE_SIZE,
          ...listParamsRef.current,
        });
        if (seq !== requestSeqRef.current) return; // superseded by a newer fetch
        setTraces(result.traces);
        setNextPageToken(result.next_page_token);
        setPageTokenStack(stack);
        // Cache page 1 only — the slice a remount restores.
        if (stack.length === 0) {
          writeRequestCache<CachedTraces>(cacheKeyRef.current, {
            traces: result.traces,
            nextPageToken: result.next_page_token,
          });
        }
      } catch (e: any) {
        if (seq !== requestSeqRef.current) return;
        // Keep any cached rows visible on a background failure.
        if (!background) setError(e?.message ?? "Failed to load traces");
      } finally {
        if (seq === requestSeqRef.current && !background) setLoading(false);
      }
    },
    [],
  );

  // Initial load + refetch (from page 1) whenever the filters change. On a cache
  // hit we show the cached page-1 immediately and revalidate in the background
  // (no spinner); otherwise a normal load. `fetchPage` is stable (useCallback [])
  // and reads the latest params via a ref, so listing it here doesn't cause
  // extra fetches — it just satisfies the exhaustive-deps rule.
  useEffect(() => {
    const cached = readRequestCache<CachedTraces>(cacheKey);
    if (cached) {
      setTraces(cached.traces);
      setNextPageToken(cached.nextPageToken);
      setPageTokenStack([]);
      fetchPage(undefined, [], true);
    } else {
      fetchPage(undefined, []);
    }
  }, [cacheKey, fetchPage]);

  const goNext = useCallback(() => {
    if (!nextPageToken) return;
    // Push the current nextPageToken onto the stack so we can go back.
    fetchPage(nextPageToken, [...pageTokenStack, nextPageToken]);
  }, [nextPageToken, pageTokenStack, fetchPage]);

  const goPrev = useCallback(() => {
    if (pageTokenStack.length === 0) return;
    const newStack = [...pageTokenStack];
    newStack.pop(); // drop the token that got us to the current page
    // token for the previous page (undefined = page 1)
    fetchPage(newStack[newStack.length - 1], newStack);
  }, [pageTokenStack, fetchPage]);

  const updateTraceRating = useCallback((id: string, rating: boolean) => {
    setTraces((prev) => prev.map((t) => (t.id === id ? { ...t, rating } : t)));
  }, []);

  const appendTraceComment = useCallback((id: string, comment: TraceComment) => {
    setTraces((prev) =>
      prev.map((t) =>
        t.id === id ? { ...t, comments: [...t.comments, comment] } : t,
      ),
    );
  }, []);

  return {
    traces,
    loading,
    error,
    hasPrev,
    hasNext,
    currentPage,
    goNext,
    goPrev,
    updateTraceRating,
    appendTraceComment,
  };
}
